import { useState } from "react";
import { adminHttp, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const CATEGORIES = ["Premium Projects", "Market Insights", "Lifestyle", "Off-Market", "Architecture"];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function BlogForm({ initial, mode, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminHttp.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, cover_image: data.url });
      toast.success("Cover uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) {
      toast.error("Title and slug are required.");
      return;
    }
    if (!form.cover_image) {
      toast.error("Cover image required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, read_minutes: parseInt(form.read_minutes || 1, 10) };
      if (mode === "create") {
        await adminHttp.post("/admin/blogs", payload);
        toast.success("Article published.");
      } else {
        await adminHttp.put(`/admin/blogs/${initial.slug}`, payload);
        toast.success("Article updated.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="admin-blog-form" onSubmit={submit} className="space-y-10">
      <div className="flex items-center justify-between">
        <button
          type="button"
          data-testid="admin-blog-form-back"
          onClick={onCancel}
          className="inline-flex items-center gap-2 underline-label text-[#6a6259] hover:text-[#c5a059]"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <h2 className="font-display text-2xl text-[#1c1c1c]">
          {mode === "create" ? "New Article" : `Edit · ${initial.title}`}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Title" required>
          <input
            data-testid="blog-form-title"
            className="editorial-input"
            value={form.title}
            onChange={(e) => {
              const title = e.target.value;
              setForm({ ...form, title, slug: mode === "create" ? slugify(title) : form.slug });
            }}
            required
          />
        </Field>
        <Field label="Slug" required>
          <input
            data-testid="blog-form-slug"
            className="editorial-input"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
            disabled={mode === "edit"}
            required
          />
        </Field>
        <Field label="Category">
          <select data-testid="blog-form-category" className="editorial-input bg-transparent" value={form.category} onChange={set("category")}>
            {CATEGORIES.map((c) => <option key={c} className="bg-[#ede5d5]" value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Read minutes">
          <input data-testid="blog-form-read_minutes" type="number" min="1" className="editorial-input" value={form.read_minutes} onChange={set("read_minutes")} />
        </Field>
        <Field label="Published date">
          <input data-testid="blog-form-published_at" type="date" className="editorial-input" value={form.published_at} onChange={set("published_at")} />
        </Field>
      </div>

      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Excerpt">
          <textarea data-testid="blog-form-excerpt" rows={3} className="editorial-input resize-none" value={form.excerpt} onChange={set("excerpt")} />
        </Field>
      </div>

      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <Field label="Body">
          <textarea data-testid="blog-form-body" rows={10} className="editorial-input resize-none font-mono text-sm" value={form.body} onChange={set("body")} placeholder="Markdown-friendly. Use blank lines for paragraph breaks." />
        </Field>
      </div>

      <div className="border-t border-[#1c1c1c]/10 pt-8">
        <div className="underline-label mb-3">Cover Image</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="aspect-[5/4] bg-[#ede5d5] border border-[#1c1c1c]/10 overflow-hidden">
            {form.cover_image ? (
              <img src={resolveImageUrl(form.cover_image)} alt="cover" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#6a6259] text-xs uppercase tracking-[0.28em]">
                No cover
              </div>
            )}
          </div>
          <div className="md:col-span-2 space-y-4">
            <label className="block">
              <div className="underline-label mb-2 text-[#6a6259]">Upload</div>
              <input
                data-testid="blog-form-cover-upload"
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])}
                disabled={uploading}
                className="block w-full text-sm text-[#1c1c1c] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#c5a059] file:text-black file:uppercase file:tracking-[0.28em] file:text-[10px] hover:file:bg-[#1c1c1c] hover:file:text-[#f5f0e8]"
              />
            </label>
            <label className="block">
              <div className="underline-label mb-2 text-[#6a6259]">Or paste a URL</div>
              <input
                data-testid="blog-form-cover-url"
                type="text"
                className="editorial-input"
                value={form.cover_image}
                onChange={set("cover_image")}
                placeholder="https://…"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1c1c1c]/10 pt-8 flex justify-end gap-3">
        <button
          type="button"
          data-testid="blog-form-cancel"
          onClick={onCancel}
          className="px-6 py-3 border border-[#c5a059]/30 text-[#6a6259] uppercase text-[10px] tracking-[0.32em] hover:text-[#1c1c1c]"
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="blog-form-submit"
          disabled={busy}
          className="inline-flex items-center gap-3 px-7 py-3 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors disabled:opacity-60"
        >
          <Save size={13} />
          {busy ? "Saving…" : mode === "create" ? "Publish" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="underline-label mb-3 text-[#6a6259]">
        {label}{required && <span className="text-[#c5a059] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
